/**
 * usePwaLifecycle — single mount-once side effect that handles two PWA
 * concerns:
 *
 *   1. Toast when a new service-worker version is waiting. sw-register.js
 *      dispatches `compass:sw-update` with an `applyUpdate()` callback —
 *      we surface a Sonner toast with an "Actualiser" action.
 *   2. Track standalone-mode visits once per session. Useful to measure
 *      PWA stickiness over time. Throttled via sessionStorage so a hot
 *      reload during dev doesn't spam the endpoint.
 *
 * The hook is fire-and-forget — analytics failures are swallowed so the
 * app never breaks because the tracking endpoint is down.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';

const KEY_STANDALONE_TRACKED = 'compass_standalone_tracked';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const trackEvent = (event) => {
  try {
    fetch(`${API_BASE}/api/pwa/track`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    }).catch(() => {});
  } catch (_) { /* noop */ }
};

export function usePwaLifecycle() {
  useEffect(() => {
    // 1) Update toast
    const onUpdate = (e) => {
      const applyUpdate = e.detail?.applyUpdate;
      toast('Nouvelle version disponible', {
        description: 'Recharge pour profiter des dernières améliorations.',
        action: {
          label: 'Actualiser',
          onClick: () => applyUpdate?.(),
        },
        duration: Infinity,
      });
    };
    window.addEventListener('compass:sw-update', onUpdate);

    // 2) Standalone-visit tracking (once per session)
    const inStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (inStandalone && !sessionStorage.getItem(KEY_STANDALONE_TRACKED)) {
      sessionStorage.setItem(KEY_STANDALONE_TRACKED, '1');
      trackEvent('standalone_visit');
    }

    return () => window.removeEventListener('compass:sw-update', onUpdate);
  }, []);
}
